import classNames from "classnames";
import { useState } from "react";
import * as React from "react";
import { useController } from "react-hook-form";
import styles from "./index.module.css";
import type { MutableRefObject } from "react";
import type {
  FieldError,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  inputClassName?: string;
  error?: FieldError;
  customErrorMessage?: string;
  customRef?: MutableRefObject<HTMLInputElement | null>;
} & UseControllerProps<TFieldValues, TName> &
  Omit<
    React.JSX.IntrinsicElements["input"],
    "name" | "defaultValue" | "onFocus"
  >;

export const Input = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  inputClassName,
  control,
  name,
  rules,
  defaultValue,
  customRef,
  error,
  ...rest
}: Props<TFieldValues, TName>): React.ReactElement => {
  const {
    field: { ref, ...inputProps },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  // Enterキー押下時に日本語入力の確定なのか否かを判定するためのstate
  const [isComposing, setIsComposing] = useState(false);

  return (
    <input
      className={classNames(
        styles.input,
        error && styles.error,
        inputClassName
      )}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={() => setIsComposing(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isComposing) {
          e.currentTarget.blur();
        }
      }}
      {...inputProps}
      {...rest}
      ref={(e) => {
        ref(e);
        if (customRef) {
          customRef.current = e;
        }
      }}
    />
  );
};

export default Input;
